import {
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type ContactDetailModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Sticker,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    CalendarIcon,
    CheckIcon,
    PhoneIcon,
    MailIcon,
    UserIcon,
    XIcon
} from "lucide-react-native";
import moment from "moment";

const ContactDetailModal = ({
    isVisible,
    onResolve,
    onClose,
    item
}: ContactDetailModalProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    if(!item) {
        return null;
    }

    const isItemAuth = Boolean(item.isAuth ?? item.isAuthenticated);
    const messageContent = item.message || item.description || "";

    return <Modal
        portalName="modal-system"
        id="contact-detail-modal"
        isActive={isVisible}
        onClosed={onClose}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingXl,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={stylesheet.headerContainer}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleMediumSize"
                >
                    {localize("requests-contact-detail-title")}
                </Text>
                <TouchableOpacity
                    style={stylesheet.closeButton}
                    onPress={onClose}
                >
                    <XIcon
                        color={colors.content.icon.mid}
                        size={24}
                    />
                </TouchableOpacity>
            </View>
            <ScrollView
                style={stylesheet.contentContainer}
                showsVerticalScrollIndicator={true}
            >
                <View
                    style={{
                        gap: spaces.spacingLg
                    }}
                >
                    <View
                        style={[
                            stylesheet.senderCardContainer,
                            {
                                backgroundColor: colors.content.container.subtle,
                                borderColor: colors.content.border.subtle,
                                borderWidth: borders.seperator,
                                borderRadius: radiuses.md,
                                padding: spaces.spacingMd,
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.headerContainer,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.infoRow,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <UserIcon
                                    color={colors.content.icon.high}
                                    size={18}
                                />
                                <Text
                                    customColor={colors.content.text.high}
                                    variant="titleSmallSize"
                                >
                                    {item.fullName}
                                </Text>
                            </View>
                            <View
                                style={[
                                    stylesheet.infoRow,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <Sticker
                                    title={isItemAuth ? localize("requests-contact-badge-authenticated") : localize("requests-contact-badge-guest")}
                                    type={isItemAuth ? "success" : "neutral"}
                                    size="small"
                                />
                                <Sticker
                                    title={item.status === "resolved" ? localize("requests-contact-status-resolved") : localize("requests-contact-status-pending")}
                                    type={item.status === "resolved" ? "success" : "warning"}
                                    size="small"
                                />
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.infoRow,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <CalendarIcon
                                color={colors.content.icon.low}
                                size={14}
                            />
                            <Text
                                customColor={colors.content.text.low}
                                variant="labelSmallSize"
                            >
                                {moment(item.createdAt).format("DD.MM.YYYY HH:mm")}
                            </Text>
                        </View>
                        <View
                            style={[
                                stylesheet.infoRow,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <MailIcon
                                color={colors.content.icon.mid}
                                size={14}
                            />
                            <Text
                                customColor={colors.content.text.mid}
                                variant="bodySmallSize"
                            >
                                {item.mail}
                            </Text>
                        </View>
                        {Boolean(item.phoneNumber) && <View
                            style={[
                                stylesheet.infoRow,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <PhoneIcon
                                color={colors.content.icon.mid}
                                size={14}
                            />
                            <Text
                                customColor={colors.content.text.mid}
                                variant="bodySmallSize"
                            >
                                {item.phoneNumber}
                            </Text>
                        </View>}
                    </View>
                    <View
                        style={[
                            stylesheet.messageSectionContainer,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelMediumSize"
                        >
                            {localize("requests-contact-subject")}
                        </Text>
                        <Text
                            customColor={colors.content.text.high}
                            variant="titleSmallSize"
                        >
                            {item.subject}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.messageSectionContainer,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelMediumSize"
                        >
                            {localize("requests-contact-message")}
                        </Text>
                        <View
                            style={[
                                stylesheet.messageCardContainer,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    borderColor: colors.content.border.subtle,
                                    borderWidth: borders.seperator,
                                    borderRadius: radiuses.md,
                                    padding: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text
                                customColor={colors.content.text.high}
                                variant="bodyMediumSize"
                            >
                                {messageContent || "-"}
                            </Text>
                        </View>
                    </View>
                    {
                        item.status === "resolved" && item.resolvedAt ?
                            <View
                                style={[
                                    stylesheet.messageSectionContainer,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <Text
                                    customColor={colors.content.text.low}
                                    variant="labelMediumSize"
                                >
                                    {localize("requests-processed-date")}
                                </Text>
                                <Text
                                    customColor={colors.content.text.high}
                                    variant="bodySmallSize"
                                >
                                    {moment(item.resolvedAt).format("DD.MM.YYYY HH:mm")}
                                </Text>
                            </View>
                        :
                            null
                    }
                </View>
            </ScrollView>
            <View
                style={[
                    stylesheet.footerContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    title={localize("notifications-detail-close")}
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    onPress={onClose}
                    variant="filled"
                    size="medium"
                />
                {
                    item.status !== "resolved" && onResolve ?
                        <Button
                            title={localize("requests-contact-resolve")}
                            icon={({
                                color,
                                size
                            }) => <CheckIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => onResolve(item.id)}
                            spreadBehaviour="free"
                            variant="filled"
                            type="success"
                            size="medium"
                        />
                    :
                        null
                }
            </View>
        </View>
    </Modal>;
};
export default ContactDetailModal;
